import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDateLocale,
  getDateInputPattern,
  DateLocaleConfig,
} from '../config/dateLocales';
import { getDateBucketType } from '../utils/getDateBucketType';

/**
 * Date formatting function type
 */
type DateFormatter = (date: Date | string | null | undefined) => string;

/**
 * Relative time formatting function type
 */
type RelativeTimeFormatter = (date: Date | string | null | undefined) => string;

/**
 * Date range formatting function type
 */
type DateRangeFormatter = (
  startDate: Date | string | null,
  endDate: Date | string | null
) => string;

/**
 * Date parsing function type
 */
type DateParser = (dateString: string) => Date | null;

/**
 * Hook return type with all date formatting functions
 */
export interface UseLocalizedDateReturn {
  /** Format date in short format (e.g., "15 sty 2025" / "Jan 15, 2025") */
  formatDateShort: DateFormatter;
  /** Format date in compact format (e.g., "15.1.25" / "1/15/25") - for pills/chips */
  formatDateCompact: DateFormatter;
  /** Format date with time (e.g., "15 sty 2025, 14:30" / "Jan 15, 2025, 2:30 PM") */
  formatDate: DateFormatter;
  /** Format date in long format (e.g., "poniedziałek, 15 stycznia 2025" / "Monday, January 15, 2025") */
  formatDateLong: DateFormatter;
  /** Format time only (e.g., "14:30" / "2:30 PM") */
  formatTime: DateFormatter;
  /** Format relative time (e.g., "5 minut temu" / "5 minutes ago") */
  formatRelativeTime: RelativeTimeFormatter;
  /** Format comment timestamp: < 1min → relative ("Just now"), >= 1min → absolute date + time */
  formatCommentTimestamp: RelativeTimeFormatter;
  /** Format date range (e.g., "15-20 sty 2025" / "Jan 15-20, 2025") */
  formatDateRange: DateRangeFormatter;
  /** Parse date string to Date object */
  parseDate: DateParser;
  /** Get current locale configuration */
  locale: DateLocaleConfig;
  /** Check if date is overdue */
  isOverdue: (date: Date | string | null | undefined) => boolean;
  /** Get days until due date */
  getDaysUntil: (date: Date | string | null | undefined) => number;
  /** Format for date input fields */
  formatForInput: DateFormatter;
  /** Format day separator label (e.g., "czwartek, 3 mar 2022" / "Thursday, Mar 3, 2022") */
  formatDaySeparator: DateFormatter;
  /** Format date bucket label - "Dzis", "Wczoraj", "Poniedzialek", "8 kwietnia" */
  formatDateBucket: DateFormatter;
  /** Format event time - "14:32" for today, "11.04 14:32" for this year, "11.04.2025" for older */
  formatEventTime: DateFormatter;
}

/**
 * Custom hook for localized date formatting
 * Provides a comprehensive set of date formatting functions that automatically
 * adapt to the current application language
 *
 * @returns Object with date formatting functions and utilities
 */
export const useLocalizedDate = (): UseLocalizedDateReturn => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;

  // Get locale configuration for current language
  const locale = useMemo(
    () => getDateLocale(currentLanguage),
    [currentLanguage]
  );

  // Memoized formatters to avoid recreation on every render
  const formatters = useMemo(() => {
    const shortFormatter = new Intl.DateTimeFormat(
      locale.locale,
      locale.formats.short
    );
    const compactFormatter = new Intl.DateTimeFormat(
      locale.locale,
      locale.formats.compact
    );
    const longFormatter = new Intl.DateTimeFormat(
      locale.locale,
      locale.formats.long
    );
    const timeFormatter = new Intl.DateTimeFormat(
      locale.locale,
      locale.formats.time
    );
    const fullFormatter = new Intl.DateTimeFormat(
      locale.locale,
      locale.formats.full
    );
    const inputFormatter = new Intl.DateTimeFormat(
      locale.locale,
      locale.formats.input
    );

    const daySeparatorFormatter = new Intl.DateTimeFormat(locale.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const weekdayFormatter = new Intl.DateTimeFormat(locale.locale, {
      weekday: 'long',
    });

    const dateBucketThisYearFormatter = new Intl.DateTimeFormat(locale.locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const dateBucketOlderFormatter = new Intl.DateTimeFormat(locale.locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const eventTimeThisYearFormatter = new Intl.DateTimeFormat(locale.locale, {
      day: 'numeric',
      month: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      short: shortFormatter,
      compact: compactFormatter,
      long: longFormatter,
      time: timeFormatter,
      full: fullFormatter,
      input: inputFormatter,
      daySeparator: daySeparatorFormatter,
      weekday: weekdayFormatter,
      dateBucketThisYear: dateBucketThisYearFormatter,
      dateBucketOlder: dateBucketOlderFormatter,
      eventTimeThisYear: eventTimeThisYearFormatter,
    };
  }, [locale]);

  // Helper function to safely convert input to Date
  const toDate = (input: Date | string | null | undefined): Date | null => {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    if (typeof input === 'string') {
      const date = new Date(input);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  // Format date in short format
  const formatDateShort: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');
    return formatters.short.format(dateObj);
  };

  // Format date in compact format (for pills/chips)
  const formatDateCompact: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');
    return formatters.compact.format(dateObj);
  };

  // Format date with time
  const formatDate: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');
    return formatters.full.format(dateObj);
  };

  // Format date in long format
  const formatDateLong: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');
    return formatters.long.format(dateObj);
  };

  // Format time only
  const formatTime: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');
    return formatters.time.format(dateObj);
  };

  const justNowLabel = t('history.relativeTime.justNow');

  // Format relative time
  const formatRelativeTime: RelativeTimeFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');

    const now = new Date();
    const diff = now.getTime() - dateObj.getTime();

    // If date is in the future, show absolute date
    if (diff < 0) {
      return formatDateShort(dateObj);
    }

    // Just now (within 1 minute)
    if (diff < locale.relativeTime.justNow) {
      return justNowLabel;
    }

    // Minutes ago
    if (diff < locale.relativeTime.minutes) {
      const minutes = Math.floor(diff / (1000 * 60));
      if (currentLanguage === 'pl') {
        return `${minutes} ${minutes === 1 ? 'minutę' : minutes < 5 ? 'minuty' : 'minut'} temu`;
      }
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }

    // Hours ago
    if (diff < locale.relativeTime.hours) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (currentLanguage === 'pl') {
        return `${hours} ${hours === 1 ? 'godzinę' : hours < 5 ? 'godziny' : 'godzin'} temu`;
      }
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }

    // Days ago
    if (diff < locale.relativeTime.days) {
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      if (currentLanguage === 'pl') {
        return `${days} ${days === 1 ? 'dzień' : 'dni'} temu`;
      }
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }

    // More than a week ago - show absolute date
    return formatDateShort(dateObj);
  };

  // Format comment timestamp: < 1min relative, >= 1min absolute date + time
  const formatCommentTimestamp: RelativeTimeFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');

    const now = new Date();
    const diff = now.getTime() - dateObj.getTime();

    // Just now (within 1 minute)
    if (diff >= 0 && diff < locale.relativeTime.justNow) {
      return justNowLabel;
    }

    // >= 1 minute or future dates: show absolute date + time
    return `${formatDateShort(dateObj)}, ${formatTime(dateObj)}`;
  };

  // Format date range
  const formatDateRange: DateRangeFormatter = (startDate, endDate) => {
    const start = toDate(startDate);
    const end = toDate(endDate);

    if (!start && !end) return t('fields.notSet');
    if (!start) return formatDateShort(end);
    if (!end) return formatDateShort(start);

    // Same day
    if (start.toDateString() === end.toDateString()) {
      return formatDateShort(start);
    }

    // Same month and year
    if (
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    ) {
      if (currentLanguage === 'pl') {
        return `${start.getDate()}-${end.getDate()} ${formatters.short.formatToParts(start).find((p) => p.type === 'month')?.value} ${start.getFullYear()}`;
      } else {
        return `${formatters.short.formatToParts(start).find((p) => p.type === 'month')?.value} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
      }
    }

    // Different months or years
    return `${formatDateShort(start)} - ${formatDateShort(end)}`;
  };

  // Parse date string
  const parseDate: DateParser = (dateString) => {
    if (!dateString) return null;

    // Get locale-specific input pattern
    const inputPattern = getDateInputPattern(currentLanguage);

    // Try different date formats
    const formats = [
      // ISO format
      { regex: /^(\d{4})-(\d{2})-(\d{2})$/, type: 'iso' },
      // Locale-specific format (DD.MM.YYYY for PL, MM/DD/YYYY for EN)
      { regex: inputPattern.regex, type: 'locale' },
    ];

    for (const format of formats) {
      const match = dateString.match(format.regex);
      if (match) {
        let year, month, day;

        if (format.type === 'iso') {
          // ISO format: YYYY-MM-DD
          [, year, month, day] = match;
        } else if (currentLanguage === 'pl') {
          // Polish format: DD.MM.YYYY
          [, day, month, year] = match;
        } else {
          // US format: MM/DD/YYYY
          [, month, day, year] = match;
        }

        if (year && month && day) {
          const date = new Date(
            Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
          );
          // Validate the date
          if (
            date.getFullYear() === parseInt(year) &&
            date.getMonth() === parseInt(month) - 1 &&
            date.getDate() === parseInt(day)
          ) {
            return date;
          }
        }
      }
    }

    // Fallback to Date constructor
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Check if date is overdue
  const isOverdue = (date: Date | string | null | undefined): boolean => {
    const dateObj = toDate(date);
    if (!dateObj) return false;
    return new Date() > dateObj;
  };

  // Get days until due date
  const getDaysUntil = (date: Date | string | null | undefined): number => {
    const dateObj = toDate(date);
    if (!dateObj) return 0;

    const now = new Date();
    const timeDiff = dateObj.getTime() - now.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  // Format day separator label
  const formatDaySeparator: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');
    return formatters.daySeparator.format(dateObj);
  };

  // Format for input fields
  const formatForInput: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return '';
    return formatters.input.format(dateObj);
  };

  // Format date bucket label for history timeline dividers
  const formatDateBucket: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');

    const bucketType = getDateBucketType(dateObj);

    switch (bucketType) {
      case 'today':
        return t('history.dateBuckets.today');
      case 'yesterday':
        return t('history.dateBuckets.yesterday');
      case 'thisWeek':
        return formatters.weekday.format(dateObj);
      case 'thisYear':
        return formatters.dateBucketThisYear.format(dateObj);
      case 'older':
        return formatters.dateBucketOlder.format(dateObj);
    }
  };

  // Format event time: time-only for today, date+time for this year, date for older
  const formatEventTime: DateFormatter = (date) => {
    const dateObj = toDate(date);
    if (!dateObj) return t('fields.notSet');

    const bucketType = getDateBucketType(dateObj);

    switch (bucketType) {
      case 'today':
        return formatters.time.format(dateObj);
      case 'yesterday':
      case 'thisWeek':
      case 'thisYear':
        return formatters.eventTimeThisYear.format(dateObj);
      case 'older':
        return formatters.compact.format(dateObj);
    }
  };

  return {
    formatDateShort,
    formatDateCompact,
    formatDate,
    formatDateLong,
    formatTime,
    formatRelativeTime,
    formatCommentTimestamp,
    formatDateRange,
    parseDate,
    locale,
    isOverdue,
    getDaysUntil,
    formatForInput,
    formatDaySeparator,
    formatDateBucket,
    formatEventTime,
  };
};
